import { type ReactNode, createElement, useEffect } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { createClientOnlyFn } from "@tanstack/react-start";
import { useRouter } from "@tanstack/react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import _ from "lodash";

export const isClient = typeof window !== "undefined";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const useScrollHashUpdater = createClientOnlyFn((sectionNames: string[]) => {
  const router = useRouter();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isClient || isMobile === true) return;

    let hashUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastClosestIndex: number | null = null;

    const handleScroll = () => {
      let closestIndex = 0;
      let closestDistance = Infinity;

      sections.forEach((section, i) => {
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const top = rect.top - 325;
        const bottom = rect.bottom - 325;
        //Bottom and top are adjusted to compensate for the fixed header.

        const distToTop = Math.abs(top);
        const distToBottom = Math.abs(bottom);
        const isVisible = bottom > 0 && top < window.innerHeight;
        const dist = isVisible ? Math.min(distToTop, distToBottom) : distToTop;

        if (dist < closestDistance) {
          closestDistance = dist;
          closestIndex = i;
        }
      });

      if (closestIndex === lastClosestIndex) return;
      lastClosestIndex = closestIndex;

      const activeSection = sections[closestIndex];
      if (!activeSection) return;

      const id = activeSection.id;

      clearTimeout(hashUpdateTimeout || undefined);

      hashUpdateTimeout = setTimeout(() => {
        if (window.location.hash !== `#${id}`) {
          // Temporarily remove ID to prevent jump
          activeSection.removeAttribute("id");

          // Update the hash silently
          history.replaceState(null, "", `#${id}`);

          setTimeout(() => {
            activeSection.id = id;
          }, 50);
        }
      }, 150);
    };

    const sectionIds = sectionNames.map((name) => name?.toLowerCase().replace(/ /g, "-"));
    const sections = sectionIds.map((id) => document.getElementById(id));

    const mainContentContainer = document.getElementById("main-content-container");
    if (mainContentContainer) {
      mainContentContainer.addEventListener("scroll", handleScroll);
    }

    // Cleanup function
    return () => {
      if (mainContentContainer) {
        mainContentContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [router, sectionNames, isMobile]);
});

// Zero-dependency allowlist HTML sanitizer — works in both browser and SSR (no Node.js built-ins).
// Only allows basic formatting tags the app actually uses in server-provided setting descriptions.
const ALLOWED_TAGS = new Set([
  "b",
  "i",
  "em",
  "strong",
  "br",
  "p",
  "ul",
  "ol",
  "li",
  "div",
  "span",
  "a",
  "dl",
  "dt",
  "dd",
  "mark",
  "code",
]);
// Subset of tags that may carry safe attributes
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  span: new Set(["class"]),
  mark: new Set(["class"]),
  a: new Set(["href", "target", "rel"]),
};

export const sanitizeHtml = (dirtyHtml: unknown): string => {
  if (typeof dirtyHtml !== "string") return "";
  // Remove dangerous block-level tags and everything they contain
  const stripped = dirtyHtml.replace(
    /<(script|style|iframe|object|embed|form|input|button|textarea|select|base|link|meta)\b[^>]*>[\s\S]*?<\/\1>/gi,
    ""
  );

  return stripped.replace(/<(\/?[a-z][a-z0-9]*)\b([^>]*)>/gi, (match, rawTag: string, attrs: string) => {
    const tag = rawTag.replace(/^\//, "").toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) return "";

    // Closing tag
    if (rawTag.startsWith("/")) return `</${tag}>`;

    // Self-closing (e.g. <br/>)
    const selfClose = match.trimEnd().endsWith("/>") ? " /" : "";

    const allowedAttrNames = ALLOWED_ATTRS[tag];
    if (!allowedAttrNames) return `<${tag}${selfClose}>`;

    // Retain only explicitly allowed attributes, stripping values that look like JS
    const safeAttrs = [...attrs.matchAll(/([a-z][a-z0-9-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi)]
      .filter(([, name]) => allowedAttrNames.has(name.toLowerCase()))
      .filter(([, , dq, sq]) => !/javascript\s*:/i.test(dq ?? sq ?? ""))
      .map(([, name, dq, sq]) => `${name.toLowerCase()}="${(dq ?? sq ?? "").replace(/"/g, "&quot;")}"`);

    return safeAttrs.length ? `<${tag} ${safeAttrs.join(" ")}${selfClose}>` : `<${tag}${selfClose}>`;
  });
};

export const highlightText = (text: string, searchTerm: string): ReactNode => {
  if (!searchTerm) return text;

  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);

  if (parts.length === 1) return text;

  return parts.map((part, i) =>
    i % 2 === 1 ? createElement("mark", { key: i, className: "bg-yellow-200 dark:bg-yellow-800 rounded" }, part) : part
  );
};

export const calculateResetValues = (organisationSettings: any, organisationSettingTypes: any) => {
  let affectedItems: string[] = [];
  if (_.isEmpty(organisationSettings)) return [];
  const organisationSettingsKeys = Object.keys(organisationSettings);
  organisationSettingTypes.forEach((settingGroup) => {
    settingGroup.settings?.forEach((setting) => {
      if (organisationSettingsKeys.includes(setting.name)) {
        affectedItems.push(setting.displayName);
      }
    });
  });
  return affectedItems;
};
